// Owner-only setup helper for Outreach secrets.
// This page NEVER accepts, forwards, transmits, or stores Tomas's secret values.
// It only: (1) generates a Fernet key server-side once, (2) shows a checklist
// of required env vars, (3) reads NON-SENSITIVE runtime status from the backend.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase, getAccessToken } from "../lib/supabaseClient";
import { SBLogo } from "../components/WebLayout";
import Seo from "../components/Seo";
import { toast } from "sonner";
import {
  Key, ShieldAlert, ClipboardCopy, RefreshCw, CheckCircle2, XCircle,
  AlertTriangle, ArrowLeft, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function authedFetch(path, opts = {}) {
  const token = await getAccessToken();
  const headers = { ...(opts.headers || {}), Authorization: `Bearer ${token || ""}` };
  return fetch(`${API}${path}`, { ...opts, headers });
}

const CHECKLIST = [
  { key: "google_oauth_client_id",       label: "GOOGLE_OAUTH_CLIENT_ID",       hint: "<numeric>-<hash>.apps.googleusercontent.com" },
  { key: "google_oauth_client_secret",   label: "GOOGLE_OAUTH_CLIENT_SECRET",   hint: "GOCSPX-<alphanum>" },
  { key: "google_oauth_redirect_uri",    label: "GOOGLE_OAUTH_REDIRECT_URI",    hint: "https://spotbounty.com/api/admin/outreach/oauth/callback", showsValue: true },
  { key: "google_token_encryption_key",  label: "GOOGLE_TOKEN_ENCRYPTION_KEY",  hint: "44-char Fernet key (generate below)" },
  { key: "openai_api_key",               label: "OPENAI_API_KEY",               hint: "sk-<alphanum>" },
  { key: "openai_model",                 label: "OPENAI_MODEL",                 hint: "gpt-4o-mini", showsValue: true },
  { key: "outreach_from",                label: "OUTREACH_FROM",                hint: "Tomas from SpotBounty <tomas@spotbounty.com>", showsValue: true },
  { key: "outreach_daily_limit",         label: "OUTREACH_DAILY_LIMIT",         hint: "25", showsValue: true },
];

function StatusIcon({ ok, warn }) {
  if (ok) return <CheckCircle2 size={16} className="text-[#22C55E]" />;
  if (warn) return <AlertTriangle size={16} className="text-[#F59E0B]" />;
  return <XCircle size={16} className="text-[#EF4444]" />;
}

export default function AdminOutreachSetupSecrets() {
  const nav = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatedKey, setGeneratedKey] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!data?.session) { nav("/admin/login"); return; }
      setAuthChecked(true);
    })();
  }, [nav]);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authedFetch("/api/admin/outreach/config/status");
      if (r.status === 401) { nav("/admin/login"); return; }
      if (r.ok) setStatus(await r.json());
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => { if (authChecked) loadStatus(); }, [authChecked, loadStatus]);

  async function generateFernet() {
    setGenerating(true);
    try {
      const r = await authedFetch("/api/admin/outreach/secrets/generate_fernet_key", { method: "POST" });
      if (r.status === 429) { toast.error("Slow down — 5 keys per minute max."); return; }
      if (r.status === 401) { nav("/admin/login"); return; }
      if (!r.ok) { toast.error("Could not generate key"); return; }
      const d = await r.json();
      setGeneratedKey(d.key || "");
    } finally { setGenerating(false); }
  }

  function copy(text, label) {
    if (!text) return;
    try {
      navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Clipboard not available — select and copy manually");
    }
  }

  function copyChecklist() {
    const lines = [
      "SpotBounty Outreach — Manage Publishing → Secrets checklist",
      "",
      "GOOGLE_OAUTH_CLIENT_ID=<paste from Google Cloud Console → APIs & Services → Credentials>",
      "GOOGLE_OAUTH_CLIENT_SECRET=<paste from Google Cloud Console → APIs & Services → Credentials>",
      "GOOGLE_OAUTH_REDIRECT_URI=https://spotbounty.com/api/admin/outreach/oauth/callback",
      "GOOGLE_TOKEN_ENCRYPTION_KEY=<PASTE THE GENERATED FERNET KEY HERE>",
      "OPENAI_API_KEY=<paste sk-... from platform.openai.com/api-keys>",
      "OPENAI_MODEL=gpt-4o-mini",
      "OUTREACH_FROM=Tomas from SpotBounty <tomas@spotbounty.com>",
      "OUTREACH_DAILY_LIMIT=25",
      "",
      "After entering all 8 values in Manage Publishing → Secrets, press 'Web app only → Publish Now'.",
    ].join("\n");
    copy(lines, "Checklist");
  }

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#080B0D] text-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#A1A1AA]"><Loader2 size={16} className="animate-spin text-[#FFC107]" /> Verifying…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080B0D] text-white">
      <Seo title="Outreach Setup" description="SpotBounty owner secrets checklist." path="/admin/outreach/setup-secrets" noindex />
      <header className="border-b border-[#2A2F34]">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/admin" data-testid="setup-back"><SBLogo size={18} /></Link>
          <Link to="/admin" className="text-[12px] text-[#A1A1AA] hover:text-white flex items-center gap-1"><ArrowLeft size={12} /> Back to Admin</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <div>
          <div className="sb-eyebrow">Owner-Only · Setup</div>
          <h1 className="mt-1 sb-headline text-[36px] md:text-[44px]">OUTREACH <span className="text-[#FFC107]">SECRETS.</span></h1>
        </div>

        {/* WARNING BANNER */}
        <div className="sb-card p-5 border-[#F59E0B]/50" style={{ background: "rgba(245,158,11,.06)" }} data-testid="setup-banner">
          <div className="flex items-start gap-3">
            <ShieldAlert size={20} className="text-[#F59E0B] shrink-0 mt-0.5" />
            <div className="text-[13px] leading-relaxed">
              Outreach secrets must be added to <b>Manage Publishing → Secrets</b> in the Emergent dashboard so they
              survive production Publish. <b>This page is a helper only</b> — it does NOT store secrets in the pod,
              database, or code. Use it to (1) generate a Fernet encryption key, (2) verify the Google redirect URI format,
              and (3) review the checklist of what to add in the Emergent dashboard.
            </div>
          </div>
        </div>

        {/* CHECKLIST */}
        <div className="sb-card p-5" data-testid="setup-checklist">
          <div className="flex items-center justify-between">
            <div className="sb-eyebrow">Runtime Configuration Status</div>
            <button onClick={loadStatus} disabled={loading} data-testid="setup-refresh" className="sb-btn-secondary text-[11px]" style={{ padding: "7px 12px" }}>
              {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
            </button>
          </div>
          <div className="mt-3 text-[12px] text-[#A1A1AA]">
            Reflects what the running backend sees right now. After entering values in Manage Publishing and pressing
            Publish, come back and press Refresh — all rows should turn green.
          </div>

          <div className="mt-4 divide-y divide-[#2A2F34]" data-testid="setup-checklist-rows">
            {CHECKLIST.map((item) => {
              const s = status ? status[item.key] : null;
              const present = !!(s && s.present);
              const formatOk = s ? (s.format_ok ?? s.fernet_valid ?? present) : false;
              const ok = present && formatOk;
              const warn = present && !formatOk;
              const showValue = item.showsValue && s && s.value;
              return (
                <div key={item.key} className="py-3 flex items-start gap-3" data-testid={`setup-row-${item.key}`}>
                  <div className="mt-0.5"><StatusIcon ok={ok} warn={warn} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-bold sb-mono">{item.label}</div>
                    <div className="text-[11.5px] text-[#A1A1AA]">
                      {ok ? "Configured" : (warn ? "Present but wrong format" : "Awaiting Manage Publishing")}
                      {" · "}<span className="text-[#52525b]">{item.hint}</span>
                    </div>
                    {showValue && (
                      <div className="mt-1 text-[11px] break-all"><span className="text-[#52525b]">value: </span><span className="sb-mono text-white/80">{s.value}</span></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5">
            <button onClick={copyChecklist} data-testid="setup-copy-checklist" className="sb-btn-secondary text-[11px]" style={{ padding: "8px 14px" }}>
              <ClipboardCopy size={12} /> Copy Manage Publishing checklist
            </button>
          </div>
        </div>

        {/* FERNET GENERATOR */}
        <div className="sb-card p-5" data-testid="setup-fernet">
          <div className="sb-eyebrow flex items-center gap-2"><Key size={12} className="text-[#FFC107]" /> Generate Fernet Encryption Key</div>
          <div className="mt-2 text-[13px] text-[#A1A1AA]">
            Used for <span className="sb-mono">GOOGLE_TOKEN_ENCRYPTION_KEY</span>. The server generates a fresh key on demand.
            It is <b>NOT stored anywhere</b> on our side — you must copy it now and paste it into Manage Publishing → Secrets.
          </div>

          <div className="mt-4">
            <button onClick={generateFernet} disabled={generating} data-testid="setup-generate-fernet"
              className="sb-btn-primary text-[12px]" style={{ padding: "10px 16px" }}>
              {generating ? <Loader2 size={13} className="animate-spin" /> : <Key size={13} />} Generate Fernet Key
            </button>
          </div>

          {generatedKey && (
            <div className="mt-4 sb-card p-4 border-[#22C55E]/40" style={{ background: "rgba(34,197,94,.05)" }} data-testid="setup-fernet-result">
              <div className="sb-eyebrow" style={{ fontSize: 10 }}>Generated Key · shown once</div>
              <div className="mt-2 sb-mono text-[12px] break-all bg-[#0B0E11] border border-[#2A2F34] rounded-lg p-3" data-testid="setup-fernet-value">{generatedKey}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => copy(generatedKey, "Fernet key")} data-testid="setup-fernet-copy" className="sb-btn-primary text-[11px]" style={{ padding: "8px 14px" }}>
                  <ClipboardCopy size={12} /> Copy Key
                </button>
                <button onClick={() => setGeneratedKey("")} className="sb-btn-secondary text-[11px]" style={{ padding: "8px 14px" }}>Clear from screen</button>
              </div>
              <div className="mt-3 text-[12px] text-[#F59E0B] flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  This key is shown <b>once</b>. Copy it now and paste it into Manage Publishing → Secrets as
                  <span className="sb-mono"> GOOGLE_TOKEN_ENCRYPTION_KEY</span>. If you lose it, generate a new one and
                  disconnect + reconnect Gmail (previously stored tokens will become unreadable).
                </span>
              </div>
            </div>
          )}
        </div>

        {/* WHAT THIS PAGE DOES / DOES NOT */}
        <div className="sb-card p-5">
          <div className="sb-eyebrow">Scope of this Helper</div>
          <div className="mt-3 grid md:grid-cols-2 gap-4 text-[12.5px]">
            <div>
              <div className="text-[#22C55E] font-bold mb-2">This page DOES</div>
              <ul className="space-y-1 text-[#A1A1AA] list-disc pl-4">
                <li>Generate a fresh Fernet key server-side, on demand, shown once.</li>
                <li>Show non-sensitive runtime status (present + format valid) for each Outreach env var.</li>
                <li>Copy a plain-text checklist to your clipboard for pasting into Manage Publishing.</li>
                <li>Display non-secret values for OPENAI_MODEL, OUTREACH_FROM, OUTREACH_DAILY_LIMIT, and the redirect URI.</li>
              </ul>
            </div>
            <div>
              <div className="text-[#EF4444] font-bold mb-2">This page does NOT</div>
              <ul className="space-y-1 text-[#A1A1AA] list-disc pl-4">
                <li>Accept, forward, or transmit your secret values.</li>
                <li>Write secrets to <span className="sb-mono">/app/backend/.env</span>, the database, or disk.</li>
                <li>Log secret values or return them in any error trace.</li>
                <li>Attempt to write into Manage Publishing (dashboard-only; no pod API exists).</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
